import React, { useContext, useRef, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import constants from "../../constants";
import AlertContext from "../../context/AlertContext";
import useShareScopeHook from '../../Hooks/useShareScopeHook';

const SharedScopeList = [
    { value: constants.SHARED_SCOPE.PRIVATE, label: "Private" },
    { value: constants.SHARED_SCOPE.SPECIFIC, label: "Share with Specific" },
    { value: constants.SHARED_SCOPE.PUBLIC, label: "Share with All" },
];

const Icon = ({ name = "checkMark" }) => {
    const icons = {
        checkMark: (
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="11" viewBox="0 0 15 11" fill="none">
                <path d="M14.1668 0.833313L5.00016 9.99998L0.833496 5.83331" stroke="#F55533" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        ),
    };
    return icons[name] || icons.checkMark;
};


const ShareScopeEditDropDown = ({ selectedRow, onUpdateSuccess, setDropdownOpen, setOpenModal, from, anchorRef }) => {
    const { setShowNotify } = useContext(AlertContext);
    const { updateShareScope } = useShareScopeHook();

    const listRef = useRef(null);
    const [dropUp, setDropUp] = useState(false);
    const [maxHeight, setMaxHeight] = useState(200);
    const [anchorStyle, setAnchorStyle] = useState({ left: 0, top: 'auto', bottom: 'auto', width: 'auto' });

    useEffect(() => {
        const updatePosition = () => {
            const el = listRef.current;
            if (!el) return;
            const triggerEl = anchorRef && anchorRef.current ? anchorRef.current : el.parentElement;
            if (!triggerEl) return;
            const triggerRect = triggerEl.getBoundingClientRect();

            // temporarily remove maxHeight to measure natural height
            el.style.maxHeight = 'none';
            const rect = el.getBoundingClientRect();
            const margin = 12;
            const spaceBelow = window.innerHeight - triggerRect.bottom;
            const spaceAbove = triggerRect.top;

            let up = false;
            if (rect.height + margin > spaceBelow && spaceAbove > spaceBelow) {
                up = true;
                setMaxHeight(Math.max(100, Math.floor(spaceAbove - margin)));
            } else {
                up = false;
                setMaxHeight(Math.max(100, Math.floor(spaceBelow - margin)));
            }

            setDropUp(up);

            // compute fixed-position anchor styles
            const left = Math.max(8, triggerRect.left);
            const width = Math.max(220, triggerEl.offsetWidth || 220);
            if (up) {
                const bottom = Math.max(6, Math.floor(window.innerHeight - triggerRect.top + 6));
                setAnchorStyle({ left, top: 'auto', bottom: `${bottom}px`, width: `${width}px` });
            } else {
                const top = Math.max(6, Math.floor(triggerRect.bottom + 6));
                setAnchorStyle({ left, top: `${top}px`, bottom: 'auto', width: `${width}px` });
            }
        };

        const rafId = requestAnimationFrame(updatePosition);
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, []);

    const warningMessage = (scope) => {
        const msg =
            scope.value === constants.SHARED_SCOPE.PUBLIC
                ? 'shared with all colleges.'
                : scope.value === constants.SHARED_SCOPE.SPECIFIC
                    ? 'shared only with selected colleges.'
                    : 'private and will not be shared with any college.';
        return msg;
    }

    const handleSelect = async (scope) => {
        setShowNotify({ show: false }); // reset notify
        if (scope.value === constants.SHARED_SCOPE.SPECIFIC) {
            setOpenModal(true);
            return;
        }
        const payload = {
            shareScope: scope.value
        };

        if (from === "topic") {
            payload.topicId = selectedRow.tid;
        } else {
            payload.lpId = selectedRow.id;
        }

        await updateShareScope(payload, onUpdateSuccess, from);

    };

    const portalContent = (
        <React.Fragment>
            {/* Invisible backdrop to close dropdown when clicking outside */}
            <div className="dropdown-backdrop" onClick={() => setDropdownOpen(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 10 }} />

            <ul
                ref={listRef}
                className={`dropdown-list scope-list ${dropUp ? 'drop-up' : ''}`}
                style={{
                    maxHeight: `${maxHeight}px`,
                    position: 'fixed',
                    left: anchorStyle.left || 0,
                    top: anchorStyle.top === 'auto' ? 'auto' : anchorStyle.top,
                    bottom: anchorStyle.bottom === 'auto' ? 'auto' : anchorStyle.bottom,
                    width: anchorStyle.width || 'auto',
                    zIndex: 100,
                    padding: 0,
                    marginBottom: 0,
                    overflowY: 'hidden',
                    border: 'none',
                }}
            >
                {SharedScopeList.map((scope) => (
                    <li key={scope.value}
                        onClick={(event) => {
                            event.stopPropagation();
                            setDropdownOpen(false); // Close dropdown immediately

                            if (scope.value === constants.SHARED_SCOPE.SPECIFIC) {
                                handleSelect(scope)
                                return;
                            }
                            setShowNotify({
                                show: true,
                                titleSvg: "warning",
                                primaryButton: "Confirm",
                                title: "Are you sure you want to modify sharing?",
                                secondaryButton: "Cancel",
                                msg: `The ${from} '${selectedRow.topic || selectedRow.name}' will be ${warningMessage(scope)}`,
                                // job: true,
                                primaryButtonFunction: () => handleSelect(scope),
                            });
                        }}
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontFamily: 'Inter',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#101828'
                        }}
                    >
                        <span className="shared-scope-label">{scope.label}</span>
                        {selectedRow.shareScope === scope.value && (
                            <span className="check-mark"><Icon name="checkMark" /></span>
                        )}
                    </li>
                ))}
            </ul>
        </React.Fragment>
    );

    return createPortal(portalContent, document.body);
}

export default ShareScopeEditDropDown;
